import { useMemo } from 'react'

/**
 * Props for useSelectAll hook
 */
type UseSelectAllProps<T> = {
  /** Currently selected options */
  selectedOptions: T[]
  /** Currently visible filtered options */
  selectableOptions: T[]
  /** Function to extract unique key from each option */
  getOptionKey: (option: T) => string
  /** Callback called on selection change */
  onSelectionChange: (selected: T[]) => void
}

/**
 * Hook to manage global selection/deselection with indeterminate states.
 *
 * This generic hook manages "select all" logic for multi-selection components.
 * It intelligently calculates the "select all" checkbox state (checked, unchecked,
 * indeterminate) based on filtered options and provides a function to toggle
 * global selection.
 *
 * Features:
 * - Automatic calculation of select all state (checked/unchecked/indeterminate)
 * - Intelligent selection/deselection of filtered options only
 * - Optimized selection Map for improved performance
 * - Generic support for any object type
 * - Filtering state management without affecting out-of-filter selections
 * - Deselection logic preserving non-filtered items
 *
 * States:
 * - checked: All filtered options are selected
 * - unchecked: No filtered option is selected
 * - indeterminate: Some filtered options are selected
 *
 * @example
 * ```tsx
 * const { selectAllState, handleSelectAll, selectedOptionsMap } = useSelectAll({
 *   selectedOptions: selectedUsers,
 *   filteredOptions: filteredUsers,
 *   getOptionKey: (user) => user.id,
 *   onSelectionChange: (users) => setSelectedUsers(users)
 * })
 *
 * // In render
 * <input
 *   type="checkbox"
 *   checked={selectAllState.checked}
 *   ref={el => el && (el.indeterminate = selectAllState.indeterminate)}
 *   onChange={handleSelectAll}
 * />
 * ```
 */
export const useSelectAll = <T>({
  selectedOptions,
  selectableOptions,
  getOptionKey,
  onSelectionChange,
}: UseSelectAllProps<T>) => {
  const selectedOptionsMap = useMemo(() => {
    const map = new Map<string, T>()
    selectedOptions.forEach(item => {
      map.set(getOptionKey(item), item)
    })
    return map
  }, [selectedOptions, getOptionKey])

  const selectAllState = useMemo(() => {
    if (!selectableOptions.length) return { checked: false, indeterminate: false }

    const filteredCount = selectableOptions.length
    let selectedCount = 0

    for (const option of selectableOptions) {
      if (selectedOptionsMap.has(getOptionKey(option))) {
        selectedCount++
      }
    }

    if (!selectedCount) return { checked: false, indeterminate: false }

    if (selectedCount === filteredCount) return { checked: true, indeterminate: false }

    return { checked: false, indeterminate: true }
  }, [selectableOptions, selectedOptionsMap, getOptionKey])

  const handleSelectAll = () => {
    if (selectAllState.checked) {
      const filteredKeys = new Set(selectableOptions.map(getOptionKey))
      const newSelected = selectedOptions.filter(item => !filteredKeys.has(getOptionKey(item)))

      onSelectionChange(newSelected)
    } else {
      const newSelected = [...selectedOptions]

      selectableOptions.forEach(option => {
        if (!selectedOptionsMap.has(getOptionKey(option))) {
          newSelected.push(option)
        }
      })
      onSelectionChange(newSelected)
    }
  }

  return {
    selectAllState,
    handleSelectAll,
    selectedOptionsMap,
  }
}
